<template>
  <div class="member-type-select">
    <el-card class="select-card">
      <template #header>
        <div class="card-header">
          <h2>选择会员类型</h2>
          <p>请根据您的需求选择合适的会员类型</p>
        </div>
      </template>

      <MemberTypeSelector
        v-model="selectedType"
        v-model:level="selectedLevel"
        :show-director="false"
        @change="handleTypeChange"
        @permissions-change="handlePermissionsChange"
      />

      <div class="action-buttons">
        <el-button size="large" @click="goBack">
          返回
        </el-button>
        <el-button type="primary" size="large" :disabled="!selectedType" @click="handleNext">
          下一步
        </el-button>
      </div>
    </el-card>

    <!-- 会员信息确认弹窗 -->
    <el-dialog
      v-model="confirmDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      :show-close="true"
      custom-class="confirm-dialog"
    >
      <template #header>
        <div class="confirm-header">
          <div class="header-logo">
            <div class="logo-icon">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              </svg>
            </div>
          </div>
          <h3 class="confirm-title">会员信息确认</h3>
          <p class="confirm-org">山东心理咨询行业协会</p>
          <div class="header-divider"></div>
        </div>
      </template>

      <div class="confirm-body">
        <!-- 会员信息表格 -->
        <div class="confirm-info-table">
          <div class="table-row">
            <div class="table-label">会员类型</div>
            <div class="table-value">{{ confirmTypeName }}</div>
          </div>
          <div class="table-row" v-if="selectedType === MEMBER_TYPE.INDIVIDUAL">
            <div class="table-label">会员等级</div>
            <div class="table-value table-value--primary">{{ confirmLevelName }}</div>
          </div>
          <div class="table-row">
            <div class="table-label">缴费年份</div>
            <div class="table-value">{{ currentYear }} 年度</div>
          </div>
          <div class="table-row table-row--highlight">
            <div class="table-label">应缴金额</div>
            <div class="table-amount" v-if="confirmFee !== null">
              <span class="amount-currency">人民币</span>
              <span class="amount-symbol">¥</span>
              <span class="amount-number">{{ confirmFee }}</span>
              <span class="amount-unit">元</span>
            </div>
            <div class="table-amount table-amount--negotiate" v-else>
              <span>面议</span>
            </div>
          </div>
        </div>

        <!-- 会员权益 -->
        <div class="confirm-benefits" v-if="permissions.length">
          <div class="benefits-title">
            <span class="title-text">会员权益</span>
            <span class="title-line"></span>
          </div>
          <div class="benefits-grid">
            <div class="benefit-item" v-for="(item, index) in permissions" :key="index">
              <div class="benefit-icon">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </div>
              <span class="benefit-text">{{ item }}</span>
            </div>
          </div>
        </div>

        <!-- 重要提示 -->
        <div class="confirm-notice">
          <div class="notice-icon">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
            </svg>
          </div>
          <div class="notice-content">
            <div class="notice-title">重要提示</div>
            <div class="notice-text">请先完善会员信息并提交审核，审核通过后方可进行缴费。</div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="confirm-footer">
          <el-button class="footer-btn footer-btn--cancel" size="large" @click="confirmDialogVisible = false">
            暂不办理
          </el-button>
          <el-button class="footer-btn footer-btn--primary" type="primary" size="large" @click="openMemberInfoDialog">
            立即完善信息
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 完善信息弹窗 -->
    <MemberInfoDialog 
      v-model="memberInfoDialogVisible" 
      :initial-data="memberInfoInitialData"
      @submit="handleMemberInfoSubmit" 
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import MemberTypeSelector from '@/components/MemberTypeSelector.vue'
import MemberInfoDialog from '@/components/MemberInfoDialog.vue'
import { MEMBER_TYPE, MEMBER_TYPE_NAMES, MEMBER_LEVEL_NAMES } from '@/utils/constants'
import api from '@/api/index'
import memberApi from '@/api/member'

export default {
  name: 'MemberTypeSelect',

  components: {
    MemberTypeSelector,
    MemberInfoDialog
  },

  data() {
    return {
      MEMBER_TYPE,
      selectedType: null,
      selectedLevel: null,
      permissions: [],
      confirmDialogVisible: false,
      confirmFee: null,
      memberInfoDialogVisible: false,
      memberInfoInitialData: {},
      isEditMode: false
    }
  },

  computed: {
    ...mapGetters('auth', ['memberInfo', 'token']),
    currentYear() {
      return new Date().getFullYear()
    },
    confirmTypeName() {
      return MEMBER_TYPE_NAMES[this.selectedType] || ''
    },
    confirmLevelName() {
      return MEMBER_LEVEL_NAMES[this.selectedLevel] || ''
    }
  },

  mounted() {
    // 如果已有会员信息，预选择
    if (this.memberInfo && this.memberInfo.member_type && this.memberInfo.member_type !== MEMBER_TYPE.DIRECTOR) {
      this.selectedType = this.memberInfo.member_type
      this.selectedLevel = this.memberInfo.member_level
    }
  },

  methods: {
    /**
     * 处理类型变化
     */
    handleTypeChange() {
      // 会员类型已选择
    },

    /**
     * 处理权益列表变化
     */
    handlePermissionsChange(list) {
      this.permissions = list
    },

    /**
     * 下一步 - 存储会员类型和等级，引导用户去完善会员信息
     */
    async handleNext() {
      if (!this.selectedType) {
        this.$message.warning('请选择会员类型')
        return
      }

      if (this.selectedType === MEMBER_TYPE.ORGANIZATION || this.selectedType === MEMBER_TYPE.DIRECTOR) {
        const memberTypeName = MEMBER_TYPE_NAMES[this.selectedType] || '该会员类型'
        this.$message.warning(`系统暂不支持${memberTypeName}的注册，请线下联系客服或管理人员。`)
        return
      }

      // 个人会员需要选择等级
      if (this.selectedType === MEMBER_TYPE.INDIVIDUAL && !this.selectedLevel) {
        this.$message.warning('请选择会员等级')
        return
      }

      // 存储会员类型和等级到本地
      localStorage.setItem('pending_member_type', this.selectedType)
      if (this.selectedLevel) {
        localStorage.setItem('pending_member_level', this.selectedLevel)
      }
      
      // 清除之前的完善状态
      localStorage.removeItem('member_info_completed')

      // 获取费用信息并显示确认弹窗
      this.confirmFee = null
      if (this.selectedType === MEMBER_TYPE.INDIVIDUAL) {
        try {
          const res = await api.getFeeConfig({
            member_type: this.selectedType,
            member_level: this.selectedLevel
          })
          if (res.code === 2000 && res.data && res.data.specific_config) {
            this.confirmFee = res.data.specific_config.fee
          }
        } catch (e) {
          console.error('获取会费配置失败:', e)
        }
      }

      // 打开确认弹窗
      this.confirmDialogVisible = true
    },

    /**
     * 去完善信息 - 打开完善信息弹窗，先查询是否已有会员信息
     */
    async openMemberInfoDialog() {
      this.confirmDialogVisible = false
      this.isEditMode = false

      try {
        const res = await memberApi.getMemberInfo()
        if (res && res.code === 2000 && res.data && res.data.id) {
          // 已有会员信息，进入编辑模式并回填表单
          this.isEditMode = true
          const d = res.data
          let experiences = []
          try {
            experiences = d.experiences ? JSON.parse(d.experiences) : []
          } catch (e) {
            experiences = []
          }
          if (!experiences.length) {
            experiences = [
              { time: [], location: '', supervisorName: '', supervisorHours: '' },
              { time: [], location: '', supervisorName: '', supervisorHours: '' }
            ]
          }
          this.memberInfoInitialData = {
            realName: d.name || '',
            gender: d.gender || '',
            phone: d.phone || '',
            email: d.email || '',
            birthday: d.birthday || '',
            company: d.company || '',
            title: d.title || '',
            education: d.education || '本科',
            city: d.city || '',
            degreeNo: d.degree_no || '',
            graduateSchool: d.graduate_school || '',
            major: d.major || '',
            startDate: d.start_date || '',
            experiences,
            isMember: d.is_member || '否',
            hasViolation: d.has_violation || '否',
            serviceCount: d.service_count || '',
            caseCount: d.case_count || '',
            remark: d.remark || '',
            photoUrl: d.photo || '',
            certificateUrl: d.certificate || '',
            degreeUrl: d.degree_certificate || ''
          }
        }
      } catch (e) {
        console.error('获取会员信息失败:', e)
      }

      this.memberInfoDialogVisible = true
    },

    /**
     * 处理会员信息提交
     */
    async handleMemberInfoSubmit(submitData) {
      try {
        // 添加会员类型和等级
        const finalData = {
          ...submitData,
          member_type: this.selectedType,
          member_level: this.selectedLevel
        }

        const res = await (this.isEditMode
          ? memberApi.updateMemberInfo(finalData)
          : memberApi.createMember(finalData))

        if (res && res.code === 2000) {
          this.$message.success(res.msg || '提交成功！')
          this.memberInfoDialogVisible = false

          // 标记会员信息已完善
          localStorage.setItem('member_info_completed', 'true')

          // 刷新会员信息
          if (res.data) {
            await this.$store.dispatch('auth/refreshMemberInfo', res.data)
          }

          // 机构会员和理事会员不支持在线支付，提交后等待审核/线下办理
          if (this.selectedType === MEMBER_TYPE.ORGANIZATION || this.selectedType === MEMBER_TYPE.DIRECTOR) {
            // 清除本地存储
            localStorage.removeItem('pending_member_type')
            localStorage.removeItem('pending_member_level')
            localStorage.removeItem('member_info_completed')
            
            this.$message.success('会员信息已提交，请等待审核')
            
            // 跳转到首页
            this.$router.push('/user/home')
          } else {
            // 个人会员进入统一的会员支付页面
            this.$router.push({
              path: '/user/member-payment',
              query: {
                type: this.selectedType,
                level: this.selectedLevel
              }
            })
          }
        }
      } catch (error) {
        console.error('提交失败:', error)
        this.$message.error('提交失败，请稍后重试')
      }
    },

    /**
     * 去完善信息 - 跳转到会员信息页面（保留作为备用）
     */
    goToMemberInfo() {
      this.openMemberInfoDialog()
    },

    /**
     * 返回
     */
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style lang="scss" scoped>
.member-type-select {
  min-height: 100vh;
  padding: 40px 20px;
  background-color: #f5f7fa;

  .select-card {
    max-width: 1400px;
    margin: 0 auto;

    .card-header {
      text-align: center;

      h2 {
        margin: 0 0 10px 0;
        font-size: 28px;
        color: #303133;
      }

      p {
        margin: 0;
        color: #909399;
        font-size: 16px;
      }
    }

    .action-buttons {
      display: flex;
      justify-content: center;
      gap: 20px;
      margin-top: 30px;
      padding-top: 30px;
      border-top: 1px solid #e4e7ed;
    }
  }
}

.confirm-header {
  text-align: center;
  padding: 0;
  background: linear-gradient(180deg, #f8f9fc 0%, #ffffff 100%);
  border-bottom: 2px solid #1a3a6b;
  position: relative;

  .header-logo {
    padding: 28px 0 16px;

    .logo-icon {
      width: 72px;
      height: 72px;
      margin: 0 auto;
      background: linear-gradient(135deg, #1a3a6b 0%, #2c5282 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      box-shadow: 0 4px 16px rgba(26, 58, 107, 0.25);
      border: 3px solid #fff;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        inset: -6px;
        border-radius: 50%;
        border: 1px solid rgba(26, 58, 107, 0.15);
      }
    }
  }

  .confirm-title {
    margin: 0 0 8px;
    font-size: 24px;
    font-weight: 700;
    color: #1a3a6b;
    letter-spacing: 2px;
    font-family: 'Microsoft YaHei', 'SimHei', sans-serif;
  }

  .confirm-org {
    margin: 0 0 20px;
    font-size: 14px;
    color: #5a6c7d;
    letter-spacing: 3px;
    font-weight: 500;
  }

  .header-divider {
    height: 3px;
    background: linear-gradient(90deg, transparent 0%, #c8a96e 50%, transparent 100%);
    margin: 0 auto;
    width: 60%;
  }
}

.confirm-body {
  padding: 32px 0 24px;

  .confirm-info-table {
    background: #ffffff;
    border: 1px solid #e0e6ed;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 24px;

    .table-row {
      display: flex;
      align-items: center;
      border-bottom: 1px solid #f0f3f7;

      &:last-child {
        border-bottom: none;
      }

      &.table-row--highlight {
        background: linear-gradient(90deg, #fafbfc 0%, #f8f9fb 100%);
        border-top: 2px solid #e0e6ed;
        border-bottom: 2px solid #e0e6ed;
      }

      .table-label {
        flex: 0 0 120px;
        padding: 16px 20px;
        background: #f8f9fc;
        border-right: 1px solid #e0e6ed;
        font-size: 14px;
        color: #5a6c7d;
        font-weight: 600;
        letter-spacing: 0.5px;
      }

      .table-value {
        flex: 1;
        padding: 16px 20px;
        font-size: 15px;
        color: #2c3e50;
        font-weight: 500;

        &.table-value--primary {
          color: #1a3a6b;
          font-weight: 600;
        }
      }

      .table-amount {
        flex: 1;
        padding: 16px 20px;
        display: flex;
        align-items: baseline;
        gap: 4px;
        color: #c8342e;
        font-weight: 600;

        .amount-currency {
          font-size: 13px;
          color: #5a6c7d;
          margin-right: 4px;
        }

        .amount-symbol {
          font-size: 18px;
        }

        .amount-number {
          font-size: 32px;
          font-weight: 700;
          font-family: 'DIN', 'Arial', sans-serif;
          line-height: 1;
        }

        .amount-unit {
          font-size: 14px;
          color: #5a6c7d;
          margin-left: 2px;
        }

        &.table-amount--negotiate {
          font-size: 20px;
          color: #c8a96e;
          font-weight: 600;
        }
      }
    }
  }

  .confirm-benefits {
    margin-bottom: 24px;

    .benefits-title {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      gap: 12px;

      .title-text {
        font-size: 15px;
        color: #1a3a6b;
        font-weight: 600;
        letter-spacing: 1px;
        white-space: nowrap;
      }

      .title-line {
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, #c8a96e 0%, transparent 100%);
      }
    }

    .benefits-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;

      .benefit-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        background: #f8f9fc;
        border: 1px solid #e8eef5;
        border-radius: 4px;
        transition: all 0.2s ease;

        &:hover {
          background: #f0f4f8;
          border-color: #c8a96e;
        }

        .benefit-icon {
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          background: #1a3a6b;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
        }

        .benefit-text {
          font-size: 13px;
          color: #2c3e50;
          line-height: 1.4;
        }
      }
    }
  }

  .confirm-notice {
    display: flex;
    gap: 12px;
    padding: 16px 18px;
    background: linear-gradient(135deg, #fffbf0 0%, #fff8e6 100%);
    border: 1px solid #f0e5c8;
    border-left: 4px solid #c8a96e;
    border-radius: 4px;

    .notice-icon {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      color: #c8a96e;
    }

    .notice-content {
      flex: 1;

      .notice-title {
        font-size: 14px;
        color: #8b7355;
        font-weight: 600;
        margin-bottom: 4px;
        letter-spacing: 0.5px;
      }

      .notice-text {
        font-size: 13px;
        color: #6b5d4f;
        line-height: 1.6;
      }
    }
  }
}

.confirm-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding-top: 8px;
  border-top: 1px solid #e8eef5;

  .footer-btn {
    min-width: 140px;
    height: 46px;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 1px;
    border-radius: 4px;
    transition: all 0.3s ease;

    &.footer-btn--cancel {
      border: 1px solid #d1d5db;
      color: #6b7280;
      background: #ffffff;

      &:hover {
        border-color: #9ca3af;
        color: #4b5563;
        background: #f9fafb;
      }
    }

    &.footer-btn--primary {
      background: linear-gradient(135deg, #1a3a6b 0%, #2c5282 100%);
      border: none;
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(26, 58, 107, 0.25);

      &:hover {
        background: linear-gradient(135deg, #2c5282 0%, #1a3a6b 100%);
        box-shadow: 0 6px 16px rgba(26, 58, 107, 0.35);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }
}

@media (max-width: 768px) {
  .member-type-select {
    padding: 20px 10px;

    .select-card {
      .card-header {
        h2 {
          font-size: 22px;
        }

        p {
          font-size: 14px;
        }
      }

      .action-buttons {
        flex-direction: column;

        .el-button {
          width: 100%;
        }
      }
    }
  }

  .confirm-header {
    .header-logo {
      padding: 24px 0 12px;

      .logo-icon {
        width: 64px;
        height: 64px;
      }
    }

    .confirm-title {
      font-size: 20px;
    }

    .confirm-org {
      font-size: 13px;
    }
  }

  .confirm-body {
    padding: 24px 0 20px;

    .confirm-info-table {
      .table-row {
        flex-direction: column;
        align-items: stretch;

        .table-label {
          flex: none;
          border-right: none;
          border-bottom: 1px solid #e0e6ed;
          padding: 12px 16px;
        }

        .table-value,
        .table-amount {
          padding: 12px 16px;
        }
      }
    }

    .confirm-benefits {
      .benefits-grid {
        grid-template-columns: 1fr;
      }
    }

    .confirm-notice {
      padding: 14px 16px;

      .notice-content {
        .notice-title {
          font-size: 13px;
        }

        .notice-text {
          font-size: 12px;
        }
      }
    }
  }

  .confirm-footer {
    flex-direction: column;
    gap: 12px;

    .footer-btn {
      width: 100%;
    }
  }
}
</style>

<style lang="scss">
/* 确认弹窗样式 - 正式庄重风格 */
.confirm-dialog {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(26, 58, 107, 0.2);

  .el-dialog__header {
    padding: 0;
    margin-right: 0;
  }

  .el-dialog__body {
    padding: 0 40px;
  }

  .el-dialog__footer {
    padding: 20px 40px 32px;
  }

  .el-dialog__headerbtn {
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    font-size: 20px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 1);
    }

    .el-dialog__close {
      color: #5a6c7d;
      transition: all 0.3s ease;

      &:hover {
        color: #1a3a6b;
      }
    }
  }
}

@media (max-width: 768px) {
  .confirm-dialog {
    width: 92% !important;
    margin: 0 auto;

    .el-dialog__body {
      padding: 0 24px;
    }

    .el-dialog__footer {
      padding: 16px 24px 24px;
    }
  }
}

/* 完善信息弹窗样式 */
.member-info-dialog {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(26, 58, 107, 0.2);

  .el-dialog__header {
    padding: 0;
    margin-right: 0;
  }

  .el-dialog__body {
    padding: 32px 40px;
    max-height: 65vh;
    overflow-y: auto;
    background: linear-gradient(180deg, #fafbfc 0%, #ffffff 100%);
  }

  .el-dialog__footer {
    padding: 0;
    border-top: 1px solid #e8eef5;
  }

  .el-dialog__headerbtn {
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    font-size: 20px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 1);
    }

    .el-dialog__close {
      color: #5a6c7d;
      transition: all 0.3s ease;

      &:hover {
        color: #1a3a6b;
      }
    }
  }

  .info-dialog-header {
    text-align: center;
    padding: 0;
    background: linear-gradient(180deg, #f8f9fc 0%, #ffffff 100%);
    border-bottom: 2px solid #1a3a6b;
    position: relative;

    .header-logo {
      padding: 28px 0 16px;

      .logo-icon {
        width: 72px;
        height: 72px;
        margin: 0 auto;
        background: linear-gradient(135deg, #1a3a6b 0%, #2c5282 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        box-shadow: 0 4px 16px rgba(26, 58, 107, 0.25);
        border: 3px solid #fff;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          inset: -6px;
          border-radius: 50%;
          border: 1px solid rgba(26, 58, 107, 0.15);
        }
      }
    }

    .info-dialog-title {
      margin: 0 0 8px;
      font-size: 24px;
      font-weight: 700;
      color: #1a3a6b;
      letter-spacing: 2px;
      font-family: 'Microsoft YaHei', 'SimHei', sans-serif;
    }

    .info-dialog-org {
      margin: 0 0 20px;
      font-size: 14px;
      color: #5a6c7d;
      letter-spacing: 3px;
      font-weight: 500;
    }

    .header-divider {
      height: 3px;
      background: linear-gradient(90deg, transparent 0%, #c8a96e 50%, transparent 100%);
      margin: 0 auto;
      width: 60%;
    }
  }

  .info-dialog-footer {
    display: flex;
    justify-content: center;
    gap: 16px;
    padding: 20px 40px 32px;
    background-color: #fafbfc;

    .footer-btn {
      min-width: 140px;
      height: 46px;
      font-size: 15px;
      font-weight: 600;
      letter-spacing: 1px;
      border-radius: 4px;
      transition: all 0.3s ease;

      &.footer-btn--cancel {
        border: 1px solid #d1d5db;
        color: #6b7280;
        background: #ffffff;

        &:hover {
          border-color: #9ca3af;
          color: #4b5563;
          background: #f9fafb;
        }
      }

      &.footer-btn--primary {
        background: linear-gradient(135deg, #1a3a6b 0%, #2c5282 100%);
        border: none;
        color: #ffffff;
        box-shadow: 0 4px 12px rgba(26, 58, 107, 0.25);

        &:hover {
          background: linear-gradient(135deg, #2c5282 0%, #1a3a6b 100%);
          box-shadow: 0 6px 16px rgba(26, 58, 107, 0.35);
          transform: translateY(-1px);
        }

        &:active {
          transform: translateY(0);
        }
      }
    }
  }

  @media (max-width: 768px) {
    width: 92% !important;
    margin: 0 auto;

    .el-dialog__body {
      padding: 24px 24px;
    }

    .info-dialog-header {
      .header-logo {
        padding: 24px 0 12px;

        .logo-icon {
          width: 64px;
          height: 64px;
        }
      }

      .info-dialog-title {
        font-size: 20px;
      }

      .info-dialog-org {
        font-size: 13px;
      }
    }

    .info-dialog-footer {
      flex-direction: column;
      gap: 12px;
      padding: 16px 24px 24px;

      .footer-btn {
        width: 100%;
      }
    }
  }
}

/* 表单样式优化 */
.member-info-form {
  .el-form-item {
    margin-bottom: 24px;

    .el-form-item__label {
      color: #1a3a6b;
      font-weight: 600;
      font-size: 14px;
      line-height: 40px;
    }

    .el-input__wrapper {
      box-shadow: 0 0 0 1px #d0d7de inset;
      border-radius: 6px;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 0 0 1px #1a3a6b inset;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px #1a3a6b inset, 0 0 0 3px rgba(26, 58, 107, 0.1);
      }
    }

    .el-textarea__inner {
      border-color: #d0d7de;
      border-radius: 6px;
      transition: all 0.3s ease;

      &:hover {
        border-color: #1a3a6b;
      }

      &:focus {
        border-color: #1a3a6b;
        box-shadow: 0 0 0 3px rgba(26, 58, 107, 0.1);
      }
    }

    .el-radio-group {
      .el-radio {
        margin-right: 20px;

        .el-radio__label {
          color: #303133;
          font-size: 14px;
        }

        &.is-checked {
          .el-radio__inner {
            background-color: #1a3a6b;
            border-color: #1a3a6b;
          }

          .el-radio__label {
            color: #1a3a6b;
            font-weight: 500;
          }
        }
      }
    }

    .el-checkbox {
      .el-checkbox__label {
        color: #303133;
        font-size: 14px;
      }

      &.is-checked {
        .el-checkbox__inner {
          background-color: #1a3a6b;
          border-color: #1a3a6b;
        }

        .el-checkbox__label {
          color: #1a3a6b;
          font-weight: 500;
        }
      }
    }
  }

  .el-divider {
    margin: 32px 0 24px;
    border-top: 2px solid #e8eef5;

    .el-divider__text {
      background-color: #fff;
      color: #1a3a6b;
      font-weight: 600;
      font-size: 15px;
      letter-spacing: 1px;
      padding: 0 20px;
      position: relative;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 16px;
        background: linear-gradient(180deg, #1a3a6b 0%, #c8a96e 100%);
        border-radius: 2px;
      }
    }
  }
}

/* 上传区域样式 */
.upload-section {
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fc 0%, #fafbfc 100%);
  border: 2px solid #e8eef5;
  border-radius: 12px;
  margin-top: 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: 20px;
    right: 20px;
    height: 2px;
    background: linear-gradient(90deg, transparent 0%, #c8a96e 50%, transparent 100%);
  }

  .el-row {
    margin: 0 -10px;
  }

  .el-col {
    padding: 0 10px;
  }

  .el-form-item {
    margin-bottom: 0;

    .el-form-item__label {
      display: none;
    }

    .el-form-item__content {
      margin-left: 0 !important;
    }
  }
}

.upload-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8eef5;
  transition: all 0.3s ease;

  &:hover {
    border-color: #c8a96e;
    box-shadow: 0 4px 12px rgba(200, 169, 110, 0.15);
  }
}

.upload-box {
  width: 140px;
  height: 140px;
  border: 2px dashed #d0d7de;
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(26, 58, 107, 0.05) 0%, rgba(200, 169, 110, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(135deg, #1a3a6b 0%, #c8a96e 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: transparent;
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(26, 58, 107, 0.2);

    &::before {
      opacity: 1;
    }

    &::after {
      opacity: 1;
    }
  }

  .upload-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
  }

  .upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #5a6c7d;
    z-index: 1;

    .el-icon {
      color: #1a3a6b;
      font-size: 32px;
      margin-bottom: 8px;
      transition: all 0.3s ease;
    }

    span {
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.5px;
    }
  }

  &:hover .upload-placeholder .el-icon {
    transform: scale(1.1);
    color: #c8a96e;
  }
}

.upload-box--id-photo {
  width: 105px;
  height: 147px;
}

.upload-tip {
  margin-top: 12px;
  color: #5a6c7d;
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
  font-weight: 500;
  padding: 6px 12px;
  background: linear-gradient(135deg, #f0f4f8 0%, #e8eef5 100%);
  border-radius: 6px;
  border: 1px solid #e0e6ed;
}

.experience-item {
  background: #fff;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: 2px solid #e8eef5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  &:hover {
    border-color: #c8a96e;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .exp-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid #f0f2f5;

    .exp-title {
      font-size: 15px;
      color: #1a3a6b;
      font-weight: 600;
      letter-spacing: 1px;
      display: flex;
      align-items: center;

      &::before {
        content: '';
        display: inline-block;
        width: 4px;
        height: 16px;
        background: linear-gradient(180deg, #1a3a6b 0%, #c8a96e 100%);
        border-radius: 2px;
        margin-right: 8px;
      }
    }

    .el-button {
      &:hover {
        color: #c8342e;
      }
    }
  }
}

/* 支付弹窗样式 */
.payment-container {
  min-height: 200px;

  &.wechat-active {
    display: flex;
    gap: 30px;
    min-height: 350px;
  }
}

.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 15px;

  .wechat-active & {
    flex: 0 0 200px;
  }

  .payment-item {
    display: flex;
    align-items: center;
    padding: 15px;
    border: 2px solid #e4e7ed;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
    position: relative;

    &:hover {
      border-color: #409eff;
      background-color: #f0f9ff;
    }

    &.active {
      border-color: #409eff;
      background-color: #ecf5ff;
    }

    .payment-icon {
      width: 32px;
      height: 32px;
      margin-right: 12px;
      object-fit: contain;
    }

    .payment-name {
      font-size: 15px;
      color: #303133;
      font-weight: 500;
      flex: 1;
    }

    .check-icon {
      font-size: 18px;
      color: #409eff;
    }
  }

  .alipay-tip {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 12px;
    background: #fdf6ec;
    border: 1px solid #faecd8;
    border-radius: 6px;
    font-size: 13px;
    color: #e6a23c;
    line-height: 1.5;

    .el-icon {
      margin-top: 2px;
      flex-shrink: 0;
    }
  }
}

.qrcode-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 30px;

  .qrcode-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    .payment-logo {
      width: 28px;
      height: 28px;
      margin-right: 10px;
    }

    .payment-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }
  }

  .qrcode-wrapper {
    width: 220px;
    height: 220px;
    background-color: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    padding: 10px;

    .qrcode-canvas {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .qrcode-loading {
      text-align: center;
      color: #909399;

      i {
        font-size: 32px;
        margin-bottom: 10px;
      }

      p {
        margin: 0;
        font-size: 14px;
      }
    }
  }

  .qrcode-tips {
    text-align: center;

    p {
      margin: 0;
      color: #606266;
      font-size: 14px;
      line-height: 1.6;
    }
  }
}
</style>
